import { BatchedQueue } from '@silo/utils';
import { Context } from 'effect';
import type { ConcurrentRateLimiterKvClient } from './kv-client';

type PromiseThunk<A = void> = () => Promise<A>;

interface Item {
  thunk: PromiseThunk;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

export interface ConcurrentRateLimiterInterface {
  queuePromise: <A>(thunk: PromiseThunk<A>) => Promise<A>;
}

export class ConcurrentRateLimiterService extends Context.Tag('ConcurrentRateLimiterService')<
  ConcurrentRateLimiterService,
  ConcurrentRateLimiterInterface
>() {}

export class ConcurrentRateLimiter implements ConcurrentRateLimiterInterface {
  #batchedQueue = new BatchedQueue<Item>();
  #kvClient: ConcurrentRateLimiterKvClient;
  #isSettling = false;

  constructor(kvClient: ConcurrentRateLimiterKvClient) {
    this.#kvClient = kvClient;

    this.#batchedQueue.onEnqueue(() => {
      if (!this.#isSettling) {
        void this.attemptToSettle();
      }
    });
  }

  private async attemptToSettle(): Promise<void> {
    if (this.#batchedQueue.size === 0) {
      return;
    }

    this.#isSettling = true;

    const requestedAllowance = this.#batchedQueue.size;

    const { allowance, ttl } = await this.#kvClient.requestAllowance(requestedAllowance);

    const promisesToSettle = this.#batchedQueue.dequeueN(allowance);

    for (const { thunk, resolve, reject } of promisesToSettle) {
      thunk().then(resolve).catch(reject);
    }

    if (allowance < requestedAllowance) {
      // eslint-disable-next-line no-console -- keeping
      console.warn(
        `Rate limit hit for key ${this.#kvClient.key}. Waiting ${ttl} seconds before attempting more requests.`
      );

      setTimeout(() => {
        void this.attemptToSettle();
      }, ttl * 1000);
    } else {
      this.#isSettling = false;
      void this.attemptToSettle();
    }
  }

  public queuePromise<A>(thunk: PromiseThunk<A>): Promise<A> {
    return new Promise<A>((resolve, reject) => {
      this.#batchedQueue.enqueue({
        thunk: thunk as PromiseThunk,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  }
}
